import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Sparkles, Palette } from 'lucide-react';
import { WOOD_THEMES as TABLE_THEMES, DEFAULT_WOOD_THEME_KEY } from '../themes/woodThemes';

const BOOKSHOP_AFFILIATE_ID = "125712";

const getBookshopLink = (book) => {
  // Search by ISBN rather than deep-linking /a/{id}/{isbn}: the direct product
  // URL 404s when Bookshop does not carry that exact edition, while an ISBN
  // search still finds sibling editions and keeps affiliate attribution.
  const query = encodeURIComponent(
    book.isbn ? book.isbn : `${book.title} ${book.author || ""}`.trim()
  );
  return `https://bookshop.org/search?keywords=${query}&affiliate=${BOOKSHOP_AFFILIATE_ID}`;
};

const THEME_STORAGE_KEY = 'll_tbr_table_theme';
const TABLE_CAP = 6;   // books that fit on the bedside table
const FLOOR_CAP = 8;   // books stacked on the floor beside it

const TbrTableStack = ({ books, onBookSelect }) => {
  const tbrBooks = books.filter(b => b.status === 'tbr');

  const [themeKey, setThemeKey] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return TABLE_THEMES[saved] ? saved : DEFAULT_WOOD_THEME_KEY;
  });
  const [showPicker, setShowPicker] = useState(false);
  // Big TBRs: the closet list starts open so overflow books are never hidden
  const [showOverflowList, setShowOverflowList] = useState(
    () => tbrBooks.length > TABLE_CAP + FLOOR_CAP
  );

  // Scale the fixed-size scene down on narrow phone screens.
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const available = containerRef.current.offsetWidth - 16;
        setScale(Math.min(1, available / SCENE_WIDTH));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const theme = TABLE_THEMES[themeKey];

  const pickTheme = (key) => {
    setThemeKey(key);
    localStorage.setItem(THEME_STORAGE_KEY, key);
  };

  const tableBooks = tbrBooks.slice(0, TABLE_CAP);
  const floorBooks = tbrBooks.slice(TABLE_CAP, TABLE_CAP + FLOOR_CAP);
  const overflowBooks = tbrBooks.slice(TABLE_CAP + FLOOR_CAP);

  const renderSpine = (book, idx, stackBottom) => {
    const rotation = (idx % 2 === 0 ? -3 : 4) + (idx * 0.5);
    const horizontalShift = (idx % 2 === 0 ? -4 : 6);
    const thickness = 14;
    const color = theme.spines[idx % theme.spines.length];

    return (
      <div
        key={book.id}
        onClick={() => onBookSelect(book)}
        style={{
          ...bookSpineStyle,
          backgroundColor: color,
          transform: `rotate(${rotation}deg) translateX(${horizontalShift}px)`,
          bottom: `${stackBottom + idx * (thickness + 3)}px`,
          zIndex: idx + 10,
        }}
        title={`Click to view: ${book.title}`}
      >
        <div style={goldFoilBandStyle} />
        <span style={{ ...bookTitleLabelStyle, color: theme.spineText }}>{book.title}</span>
        <a
          href={getBookshopLink(book)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={buySpineLinkStyle}
          title={`Buy "${book.title}" on Bookshop.org`}
        >
          📖
        </a>
        <Sparkles size={10} style={shimmerIconStyle} />
      </div>
    );
  };

  if (tbrBooks.length === 0) {
    return (
      <div style={containerStyle} ref={containerRef}>
        <div style={{ ...emptyTableStyle, transform: `scale(${scale})`, transformOrigin: 'top center' }}>
          <div style={{ ...tableSurfaceStyle, backgroundColor: theme.surface }}>
            <div style={emptyCozySetupStyle}>
              <span style={steamingCupStyle}>☕</span>
              <span style={{ fontSize: '0.8rem', color: '#8d6e63', fontStyle: 'italic' }}>
                Your bedside table is clear. Time to find a new story?
              </span>
            </div>
          </div>
          <div style={{ ...tableLegStyle, backgroundColor: theme.leg }} />
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} ref={containerRef}>
      <div style={titleStyle}>
        <BookOpen size={16} style={{ color: '#8d6e63' }} />
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#5d4037' }}>
          Bedside Reading Pile ({tbrBooks.length})
        </h3>
        <button
          onClick={() => setShowPicker(!showPicker)}
          style={paletteButtonStyle}
          title="Change table color"
          aria-label="Change table color"
        >
          <Palette size={14} style={{ color: '#8d6e63' }} />
        </button>
      </div>

      {showPicker && (
        <div style={pickerRowStyle}>
          {Object.entries(TABLE_THEMES).map(([key, t]) => (
            <button
              key={key}
              onClick={() => pickTheme(key)}
              style={{
                ...swatchStyle,
                backgroundColor: t.surface,
                outline: key === themeKey ? '2px solid #5d4037' : '1px solid #d7ccc8',
              }}
              title={t.label}
              aria-label={`${t.label} table`}
            />
          ))}
        </div>
      )}

      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top center', height: `${SCENE_HEIGHT * scale}px` }}>
        <div style={sceneStyle}>
          <div style={tableWrapperStyle}>
            {/* Steaming Mug & Candle Decor */}
            <div style={decorWrapperStyle}>
              <div style={cozyCandleStyle}>🕯️</div>
              <div style={cozyTeaStyle}>🍵</div>
            </div>

            {/* Dynamic 3D Stack of TBR Books */}
            <div style={bookStackStyle}>
              {tableBooks.map((book, idx) => renderSpine(book, idx, 0))}
            </div>

            {/* Cozy Bedside Table surface */}
            <div style={{ ...tableSurfaceStyle, backgroundColor: theme.surface }} />
            {/* Bedside table drawer / leg structure */}
            <div style={{ ...tableDrawerStyle, backgroundColor: theme.drawer }}>
              <div style={{ ...drawerKnobStyle, backgroundColor: theme.knob }} />
            </div>
            <div style={{ ...tableLegStyle, backgroundColor: theme.leg }} />
          </div>

          {/* Floor pile: books that no longer fit on the table */}
          {floorBooks.length > 0 && (
            <div style={floorPileStyle}>
              {floorBooks.map((book, idx) => renderSpine(book, idx, 0))}
              <span style={floorLabelStyle}>the floor pile 😅</span>
            </div>
          )}
        </div>
      </div>

      {overflowBooks.length > 0 && (
        <button onClick={() => setShowOverflowList(!showOverflowList)} style={overflowChipStyle}>
          {showOverflowList ? 'Tuck them away' : `+${overflowBooks.length} more hiding in the closet…`}
        </button>
      )}
      {showOverflowList && (
        <div style={overflowListStyle}>
          {overflowBooks.map((book) => (
            <div key={book.id} style={overflowRowStyle} onClick={() => onBookSelect(book)}>
              <span style={overflowTitleStyle}>{book.title}</span>
              <a
                href={getBookshopLink(book)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none', fontSize: '0.8rem' }}
                title={`Buy "${book.title}" on Bookshop.org`}
              >
                📖
              </a>
            </div>
          ))}
        </div>
      )}

      <p style={helperTextStyle}>
        💡 <em>Tap a book to view it, or tap 📖 to buy it on Bookshop.org.</em>
      </p>
    </div>
  );
};

// Styling Rules
const SCENE_WIDTH = 350;
const SCENE_HEIGHT = 230;

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '1.5rem 0.5rem',
  backgroundColor: '#fdfbf7',
  borderRadius: '12px',
  border: '1px solid #efebe9',
  boxShadow: '0 4px 15px rgba(141, 110, 99, 0.05)',
  margin: '1rem 0',
  overflow: 'hidden'
};

const titleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '1rem',
  width: '100%',
  justifyContent: 'center'
};

const paletteButtonStyle = {
  background: 'none',
  border: '1px solid #efebe9',
  borderRadius: '6px',
  padding: '4px 6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};

const pickerRowStyle = {
  display: 'flex',
  gap: '10px',
  marginBottom: '1rem'
};

const swatchStyle = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
};

const sceneStyle = {
  position: 'relative',
  width: `${SCENE_WIDTH}px`,
  height: `${SCENE_HEIGHT}px`,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start'
};

const tableWrapperStyle = {
  position: 'absolute',
  left: 0,
  bottom: '40px',
  width: '240px',
  height: '180px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const emptyTableStyle = {
  position: 'relative',
  width: '240px',
  height: '120px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end'
};

const emptyCozySetupStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  transform: 'translateY(-40px)',
  width: '220px'
};

const steamingCupStyle = {
  fontSize: '1.5rem',
  animation: 'steam 2.5s infinite ease-in-out'
};

const decorWrapperStyle = {
  position: 'absolute',
  top: '-15px',
  left: '25px',
  display: 'flex',
  gap: '12px',
  zIndex: 5
};

const cozyCandleStyle = {
  fontSize: '1.2rem',
  animation: 'flicker 1.8s infinite ease-in-out'
};

const cozyTeaStyle = {
  fontSize: '1.2rem',
  transform: 'scaleX(-1)'
};

const tableSurfaceStyle = {
  width: '100%',
  height: '16px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  zIndex: 8,
  position: 'absolute',
  bottom: '40px'
};

const tableDrawerStyle = {
  width: '200px',
  height: '35px',
  borderRadius: '0 0 6px 6px',
  position: 'absolute',
  bottom: '5px',
  zIndex: 6,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const drawerKnobStyle = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)'
};

const tableLegStyle = {
  width: '16px',
  height: '40px',
  backgroundColor: '#5d4037',
  position: 'absolute',
  bottom: '-35px',
  zIndex: 4,
  borderRadius: '0 0 4px 4px'
};

const bookStackStyle = {
  position: 'absolute',
  width: '160px',
  bottom: '54px', // resting directly on table surface
  height: '110px',
  zIndex: 9
};

const floorPileStyle = {
  position: 'absolute',
  right: '0px',
  bottom: '6px',
  width: '120px',
  height: '150px',
  zIndex: 9
};

const floorLabelStyle = {
  position: 'absolute',
  bottom: '-20px',
  left: 0,
  right: 0,
  textAlign: 'center',
  fontSize: '0.65rem',
  color: '#a1887f',
  fontStyle: 'italic'
};

const bookSpineStyle = {
  position: 'absolute',
  left: '10px',
  right: '10px',
  height: '14px',
  borderRadius: '3px 3px 2px 2px',
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.08), inset 0 -1px 2px rgba(0,0,0,0.15)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 8px',
  transition: 'all 0.2s ease-in-out',
  ':hover': {
    transform: 'scale(1.05) translateY(-2px) !important',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
};

const buySpineLinkStyle = {
  fontSize: '0.7rem',
  lineHeight: 1,
  textDecoration: 'none',
  padding: '0 2px'
};

const goldFoilBandStyle = {
  position: 'absolute',
  left: '10px',
  top: 0,
  bottom: 0,
  width: '4px',
  backgroundColor: 'rgba(255, 215, 0, 0.4)',
  borderRight: '1px solid rgba(255, 215, 0, 0.6)'
};

const bookTitleLabelStyle = {
  fontSize: '0.65rem',
  fontWeight: '600',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginLeft: '10px',
  maxWidth: '80px'
};

const shimmerIconStyle = {
  color: 'rgba(255, 255, 255, 0.8)',
  animation: 'pulse 2s infinite ease-in-out'
};

const overflowChipStyle = {
  marginTop: '0.8rem',
  padding: '6px 14px',
  borderRadius: '16px',
  border: '1px solid #efebe9',
  backgroundColor: '#f7f2ec',
  color: '#6d4c41',
  fontSize: '0.75rem',
  cursor: 'pointer'
};

const overflowListStyle = {
  width: '100%',
  maxWidth: '300px',
  marginTop: '0.6rem',
  maxHeight: '160px',
  overflowY: 'auto',
  borderRadius: '8px',
  border: '1px solid #efebe9',
  backgroundColor: '#fffdfa'
};

const overflowRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 10px',
  borderBottom: '1px solid #f3ede6',
  cursor: 'pointer'
};

const overflowTitleStyle = {
  fontSize: '0.75rem',
  color: '#5d4037',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginRight: '8px'
};

const helperTextStyle = {
  fontSize: '0.75rem',
  color: '#8d6e63',
  textAlign: 'center',
  margin: '12px 0 0 0'
};

export default TbrTableStack;
